<template>
    <section
        v-if="comparisonTable?.title1"
        :id="slice.primary.anchorName || undefined"
        class="slice slice-pricing slice-pricing--detailed"
        data-testid="pricing-detailed-slice"
    >
        <div class="slice-content">
            <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" data-testid="pricing-detailed-title" />
            <PrismicRichText
                v-if="slice.primary.description"
                :field="slice.primary.description"
                class="slice-description"
                data-testid="pricing-detailed-description"
            />
            <PricingTable :comparison-table="comparisonTable" />
            <PrismicRichText
                :field="slice.primary.footerText"
                class="slice-pricing--detailed__footer-text"
                data-testid="pricing-detailed-footer-text"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import type {ContentRelationshipField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {PricingSlice} from '@prismicio-types';
    import type {ComparisonTableDocument, ComparisonTableDocumentData} from '@prismicio-types';
    import {useAsyncData} from 'nuxt/app';
    import {usePrismicClient, usePrismicTypeGuards} from '#imports';
    import PricingTable from '../components/PricingTable.vue';

    type PricingDetailedInZone = Extract<PricingSlice, {variation: 'pricingDetailed'}>;
    const props = defineProps(getSliceComponentProps<PricingDetailedInZone>());

    const {isFilledContentRelationshipField} = usePrismicTypeGuards();
    const comparisonTable = await useComparisonTableDocument(props.slice.primary.comparisonTable);

    async function useComparisonTableDocument(comparisonTableLink: ContentRelationshipField<'comparisonTable'>) {
        const comparisonTable = ref<ComparisonTableDocumentData>();

        if (isFilledContentRelationshipField(comparisonTableLink)) {
            const {uid, lang} = comparisonTableLink;
            if (uid) {
                const client = usePrismicClient();
                const {data} = await useAsyncData(
                    `comparison-table-${uid}-${lang}`,
                    () => client.getByUID<ComparisonTableDocument>('comparisonTable', uid, {lang}),
                    {deep: true},
                );
                comparisonTable.value = data.value?.data;
            }
        }

        return comparisonTable;
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-pricing {
        &--detailed {
            .slice-title {
                margin-bottom: var(--joy-core-spacing-8);

                @include mq.screen_lg(min) {
                    margin-bottom: var(--joy-core-spacing-10);
                }
            }

            .slice-description {
                * {
                    margin-bottom: var(--joy-core-spacing-9);
                }
            }

            &__footer-text {
                text-align: left;
                margin-top: var(--joy-core-spacing-8);
            }
        }
    }
</style>
